import type { FC } from 'react';
import { useLanguage } from '../../i18n/useLanguage';

/** Inspiration block on create post — structured copy (DE/EN). */
const PostInspirationHintDe: FC = () => (
  <>
    <p>Eine gute Sichtung zu schreiben ist einfacher, als du denkst.</p>
    <p>Im Grunde erzählst du eine kleine Geschichte – über einen Moment, der fast mehr geworden wäre.</p>

    <h3 className="post-inspiration-hint-heading">📰 Die Überschrift</h3>
    <p>Die Überschrift ist dein erster Hinweis.</p>
    <p>
      Sie sollte den Ort, eine kurze Beschreibung der Person und – wenn möglich – Zeit oder Anlass
      enthalten.
    </p>
    <p>
      <strong>Beispiel:</strong>
      <br />
      „Frau mit rotem Schal im Zug von Wien nach Krems am Mittwochabend“
    </p>
    <p>Je konkreter, desto besser – so steigt die Chance, gefunden zu werden.</p>

    <h3 className="post-inspiration-hint-heading">🎬 Was ist passiert?</h3>
    <p>Hier erzählst du deine Geschichte:</p>
    <ul>
      <li>Was genau ist passiert?</li>
      <li>Wo habt ihr euch gesehen?</li>
      <li>Was ist dir an der Person aufgefallen?</li>
    </ul>
    <p>
      Und ganz wichtig: Was hat dich davon abgehalten, den Moment zu nutzen?
      <br />
      Beschreibe ruhig auch dich selbst ein wenig – das macht es greifbarer.
    </p>
    <p>
      <strong>Und vor allem: 👉 Warum möchtest du diese Person jetzt wiederfinden?</strong>
    </p>
    <p>
      Das darf charmant, ehrlich oder auch mit einem Augenzwinkern sein.
      <br />
      Es ist keine Bewerbung und kein Aufsatz – sondern ein kleiner Moment Leben.
    </p>

    <h3 className="post-inspiration-hint-heading">🕵️‍♀️ Tipps für deine Sichtung</h3>
    <ul>
      <li>
        <strong>Sei einfach du selbst.</strong>
        <br />
        Vielleicht war es genau diese Mischung aus Mut und Zögern, die dich hierher geführt hat.
      </li>
      <li>
        <strong>Sag, was dich bewegt hat.</strong>
        <br />
        Warum gerade diese Begegnung? Warum gerade jetzt?
      </li>
      <li>
        <strong>Keine Kontaktdaten im Text.</strong>
        <br />
        Bitte keine E-Mail-Adresse oder Telefonnummer angeben – dafür gibt es sichere Wege auf der
        Plattform.
      </li>
      <li>
        <strong>Bitte keine GROSSBUCHSTABEN.</strong>
        <br />
        Das wirkt nicht nur laut, sondern führt oft dazu, dass Beiträge entfernt werden.
      </li>
      <li>
        <strong>Respektvoll bleiben.</strong>
        <br />
        Beiträge mit unangemessenem Inhalt oder Absichten werden nicht veröffentlicht.
      </li>
    </ul>

    <h3 className="post-inspiration-hint-heading">📸 Nach dem Veröffentlichen</h3>
    <p>Mach deine Sichtung sichtbar:</p>
    <ul>
      <li>
        <strong>Wähle ein Profilbild.</strong>
        <br />
        Das schafft Vertrauen und erhöht die Chance, dass du entdeckt wirst.
      </li>
      <li>
        <strong>Teile deine Sichtung.</strong>
        <br />
        Über Social Media oder direkt mit Freund:innen – manchmal kennt jemand jemanden.
      </li>
    </ul>

    <h3 className="post-inspiration-hint-heading">❤️ Und jetzt?</h3>
    <p>Vielleicht war es nur ein Blick.</p>
    <p>Ein Lächeln.</p>
    <p>Ein Moment zu wenig Mut.</p>
    <p>Hier bekommst du deine zweite Chance.</p>
    <p>Also los – erzähl uns deine Geschichte.</p>
  </>
);

const PostInspirationHintEn: FC = () => (
  <>
    <p>Writing a good sighting is easier than you think.</p>
    <p>
      In essence you are telling a short story – about a moment that almost became something more.
    </p>

    <h3 className="post-inspiration-hint-heading">📰 The headline</h3>
    <p>The headline is your first hint.</p>
    <p>
      It should include the place, a brief description of the person and – when possible – time or
      occasion.
    </p>
    <p>
      <strong>Example:</strong>
      <br />
      &ldquo;Woman with a red scarf on the train from Vienna to Krems on Wednesday evening&rdquo;
    </p>
    <p>The more specific, the better – so your chances of being found go up.</p>

    <h3 className="post-inspiration-hint-heading">🎬 What happened?</h3>
    <p>Here you tell your story:</p>
    <ul>
      <li>What exactly happened?</li>
      <li>Where did you see each other?</li>
      <li>What stood out to you about the person?</li>
    </ul>
    <p>
      And very important: What stopped you from seizing the moment?
      <br />
      Feel free to describe yourself a little too – that makes it more tangible.
    </p>
    <p>
      <strong>And above all: 👉 Why do you want to find this person now?</strong>
    </p>
    <p>
      That can be charming, honest, or with a wink.
      <br />
      It is not an application or an essay – just a small moment of life.
    </p>

    <h3 className="post-inspiration-hint-heading">🕵️‍♀️ Tips for your sighting</h3>
    <ul>
      <li>
        <strong>Just be yourself.</strong>
        <br />
        Maybe it was exactly that mix of courage and hesitation that brought you here.
      </li>
      <li>
        <strong>Say what moved you.</strong>
        <br />
        Why this encounter? Why now?
      </li>
      <li>
        <strong>No contact details in the text.</strong>
        <br />
        Please do not include an email address or phone number – the platform has safe ways to
        connect.
      </li>
      <li>
        <strong>Please do not WRITE IN ALL CAPS.</strong>
        <br />
        It does not just seem loud – it often leads to posts being removed.
      </li>
      <li>
        <strong>Stay respectful.</strong>
        <br />
        Posts with inappropriate content or intentions are not published.
      </li>
    </ul>

    <h3 className="post-inspiration-hint-heading">📸 After publishing</h3>
    <p>Make your sighting visible:</p>
    <ul>
      <li>
        <strong>Choose a profile picture.</strong>
        <br />
        That builds trust and increases the chance you will be discovered.
      </li>
      <li>
        <strong>Share your sighting.</strong>
        <br />
        On social media or directly with friends – sometimes someone knows someone.
      </li>
    </ul>

    <h3 className="post-inspiration-hint-heading">❤️ And now?</h3>
    <p>Maybe it was only a glance.</p>
    <p>A smile.</p>
    <p>A moment with too little courage.</p>
    <p>Here you get your second chance.</p>
    <p>So go ahead – tell us your story.</p>
  </>
);

export const PostInspirationHintBody: FC = () => {
  const { language } = useLanguage();
  return language === 'de' ? <PostInspirationHintDe /> : <PostInspirationHintEn />;
};
